import React, { memo, useCallback, useMemo } from "react";
import { Box, CircularProgress, HStack, Image } from "@chakra-ui/react";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import colors from "app/colors";
import FileThumb from "modules/files/file-thumb";
import { DeleteFileIcon } from "modules/icons";
import {
  FILE_STATE_TYPE,
  IFileInTransit,
} from "modules/providers/index-db-provider/index-db-models";
import { useFile } from "modules/providers/file-provider";
import { isAudio, isImage } from "modules/utils/Functions";
import AudioFilePlayer from "./audio-file-player";
import { GifInput } from "gql/graphql";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { NEXT_PUBLIC_CLOUDFRONT_URL } from "app/client";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { MESSAGE_ATTACHMENT_UPLOAD_PROGRESS_Z_INDEX } from "modules/providers/chakra-provider/chakra-portal-stack";

interface IProps {
  draftId: string;
  files?: IFileInTransit[];
  gifs?: GifInput[];
}

const NewMessageUploadAttachmentList = ({ draftId, files, gifs }: IProps) => {
  const { cancel } = useFile();
  const { deleteGif } = useMessageThreadDraft({ draftId });

  const onFileCancelClicked = useCallback(
    (id: string) => () => cancel(id),
    [cancel],
  );

  const onGifCancelClicked = useCallback(
    (id?: string | null) => () => {
      if (!id) {
        return;
      }
      deleteGif(id);
    },
    [deleteGif],
  );

  if (!files?.length && !gifs?.length) {
    return null;
  }

  return (
    // mt={-1} is so the delete button is ignored when calculating y positioning
    <HStack spacing={3} py={3} overflowX="auto" width="full" mt={-1}>
      {gifs?.map((gif) => (
        <Box key={gif.id} position="relative" minW={16}>
          <CircularIconButton
            icon={<DeleteFileIcon boxSize={6} fill={colors.gray10} />}
            onClick={onGifCancelClicked(gif.id)}
            aria-label="delete"
            position="absolute"
            top={-4}
            right={-4}
            zIndex={1}
          />
          <Image
            src={gif.videoUrl ?? gif.imageUrl}
            alt={gif.name ?? ""}
            w={16}
            h={16}
            borderRadius={4}
            objectFit="cover"
          />
        </Box>
      ))}
      {files?.map((file) => (
        <Box key={file.id} position="relative">
          <CircularIconButton
            icon={<DeleteFileIcon boxSize={6} fill={colors.gray10} />}
            onClick={onFileCancelClicked(file.id)}
            aria-label="delete"
            position="absolute"
            top={-4}
            right={-4}
            zIndex={1}
          />
          {file.stateType !== FILE_STATE_TYPE.COMPLETE && (
            <CircularProgress
              position="absolute"
              isIndeterminate={
                file.percentage === 100 &&
                file.stateType === FILE_STATE_TYPE.UPLOADING
              }
              value={file.percentage}
              color={colors.blue}
              size={8}
              zIndex={MESSAGE_ATTACHMENT_UPLOAD_PROGRESS_Z_INDEX}
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
            />
          )}
          <FileItem file={file} />
        </Box>
      ))}
    </HStack>
  );
};

const FileItem: React.FC<{
  file: IFileInTransit;
}> = ({ file }) => {
  const { localFilesById } = useFile();
  const { openModal } = useOverlay();

  const onEditImageClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.EDIT_IMAGE,
      fileInTransit: file,
    });
  }, [file, openModal]);

  const onFileClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.FILE_IN_TRANSIT,
      fileInTransit: file,
    });
  }, [file, openModal]);

  const audioData = useMemo(() => {
    if (file.stateType !== FILE_STATE_TYPE.COMPLETE) {
      return null;
    }
    if (file.key) {
      return `${NEXT_PUBLIC_CLOUDFRONT_URL}/${file.key}`;
    }
    if (localFilesById[file.id]) {
      return localFilesById[file.id];
    }
    return null;
  }, [file.id, file.key, file.stateType, localFilesById]);

  if (isAudio(file.contentType)) {
    return (
      <Box
        w={200}
        maxW={200}
        h="64px"
        borderRadius={4}
        borderWidth={1}
        borderStyle="solid"
        borderColor={colors.grayPrimary}
        alignContent="center"
      >
        {audioData && (
          <AudioFilePlayer
            audioData={audioData}
            fileName={file.name}
            playIconSize={4}
          />
        )}
      </Box>
    );
  }

  if (file.localPreviewUrl) {
    return (
      <Box onClick={onEditImageClicked} cursor="pointer">
        <FileThumb
          contentType={file.contentType}
          name={file.name}
          url={file.localPreviewUrl}
          size={[64, 64]}
          fileFooterIconSize={5}
        />
      </Box>
    );
  }

  if (isImage(file.contentType)) {
    return (
      <Box onClick={onEditImageClicked} cursor="pointer">
        <FileThumb
          contentType={file.contentType}
          name={file.name}
          url={file.previewCloudFrontUrl}
          size={[64, 64]}
          fileFooterIconSize={5}
        />
      </Box>
    );
  }

  return (
    <Box onClick={onFileClicked} cursor="pointer">
      <FileThumb
        contentType={file.contentType}
        name={file.name}
        url={file.previewCloudFrontUrl}
        size={[64, 64]}
      />
    </Box>
  );
};

export default memo(NewMessageUploadAttachmentList);
